import React, { useContext, useState } from 'react';
import { v4 } from 'uuid';
import { PasswordInput, TextInput } from '../../../components/TextInput';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { t } from '../../../../types/translation/Translator';
import { Button, ButtonVariant } from '../../../components/Button/Button';
import { SignInScreenView } from '../SignInScreen';
import { AuthenticationRequest } from '../../../../requests/auth.request';
import { useRequest } from '../../../../requests/common/requestHook';
import TeamLeaderIcon from '../../../../assets/img/TeamleaderIcon.png';
import MicrosoftLogo from '../../../../assets/img/MicrosoftLogo.svg';
import { handleKeyEvent } from '../../../../util/events.util';
import { useToast } from '../../../../providers/ToastProvider';
import { ToastType } from '../../../components/Toast';
import { useNavigate } from 'react-router-dom';
import { setAccessToken } from '../../../../util/auth.util';
import { UserContext } from '../../../../context/UserContext';

interface SignInCredentialsViewProps {
  email: string;
  setEmail: (email: string) => void;

  password: string;
  setPassword: (password: string) => void;

  setView: (view: SignInScreenView) => void;
}

export default function SignInCredentialsView({
  email,
  setEmail,
  password,
  setPassword,
  setView,
}: SignInCredentialsViewProps) {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const { setCurrentUser } = useContext(UserContext);

  const [login, { loading }] = useRequest(AuthenticationRequest.login);
  const [fetchCurrentUser, {}] = useRequest(AuthenticationRequest.me);
  const [teamleaderLoading, setTeamleaderLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);

  const VentoryAuthentication = async () => {
    const data = await login({ email, password });
    if (!data) return;

    if (data.type === AuthenticationRequest.ResultType.authenticationCodeRequired) {
      showToast({
        type: ToastType.success,
        title: `An authorization code was sent to ${email}.`,
        content: 'Please enter it to continue.',
      });
      return setView(SignInScreenView.code);
    }

    setAccessToken(data.token.accessToken);

    const user = await fetchCurrentUser({});
    if (user) setCurrentUser(user);
  };

  const TeamleaderAuthentication = async () => {
    setTeamleaderLoading(true);

    const id = v4();
    const url = 'https://focus.teamleader.eu/oauth2/authorize';
    const clientId = process.env.REACT_APP_TEAMLEADER_CLIENT_ID;
    const redirectUri = encodeURIComponent(`${process.env.REACT_APP_API_BASE_URL}/api/teamleader/login`);
    const responseType = 'code';
    const state = encodeURIComponent(JSON.stringify({ k: id }));

    const link = `${url}?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=${responseType}&state=${state}`;

    window.open(link, '_self');
    setTimeout(() => setTeamleaderLoading(false), 5000);
  };

  const MicrosoftAuthentication = async () => {
    setMicrosoftLoading(true);

    const link = `${process.env.REACT_APP_API_BASE_URL}/api/microsoft/login`;
    window.open(link, '_self');

    setTimeout(() => setMicrosoftLoading(false), 5000);
  };

  return (
    <>
      <p className='my-2 text-center text-2xl font-bold'>{'Sign In'}</p>
      <TextInput
        disabled={loading || teamleaderLoading || microsoftLoading}
        value={email}
        label={t(TranslationKey.email).singular.label}
        placeholder={t(TranslationKey.email).singular.label}
        onChange={v => setEmail(v)}
      />
      <div onKeyDown={async event => handleKeyEvent(event, 'Enter', VentoryAuthentication)}>
        <PasswordInput
          disabled={loading || teamleaderLoading || microsoftLoading}
          value={password}
          label={t(TranslationKey.password).singular.label}
          placeholder={t(TranslationKey.password).singular.label}
          onChange={v => setPassword(v)}
        />
      </div>
      <Button
        loading={loading}
        disabled={teamleaderLoading || microsoftLoading}
        text={t(TranslationKey.signIn).singular.label}
        onClick={VentoryAuthentication}
      />
      <Button
        disabled={loading || teamleaderLoading || microsoftLoading}
        variant={ButtonVariant.secondary}
        text={t(TranslationKey.forgotPassword).singular.label}
        onClick={() => setView(SignInScreenView.forgotPassword)}
      />
      <p className='my-1 text-center text-xs'>{'Sign in with'}</p>
      <Button
        disabled={loading || microsoftLoading}
        loading={teamleaderLoading}
        leading={<img src={TeamLeaderIcon} className='w-8' />}
        variant={ButtonVariant.secondary}
        text={'Teamleader'}
        onClick={TeamleaderAuthentication}
      />
      <Button
        disabled={loading || teamleaderLoading || microsoftLoading}
        loading={microsoftLoading}
        leading={<img src={MicrosoftLogo} className='w-6' />}
        variant={ButtonVariant.secondary}
        text={'Microsoft'}
        onClick={MicrosoftAuthentication}
      />
    </>
  );
}
